/**
 * Helpers for v3 combined inventory preferences (allergies + dislikes in one pool).
 */
import type {
  AllergyDislikeCategoriesResponse,
  AllergyDislikesResponse,
  AllergyDislikeSelectedItem,
} from './allergyDislikesApi';

export type InventoryPreferenceType = 'allergy' | 'dislike';

function parseNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

export function normalizeIdList(raw: unknown[]): number[] {
  const out: number[] = [];
  for (const value of raw) {
    const parsed = parseNumericId(value);
    if (parsed != null) {
      out.push(parsed);
    }
  }
  return out;
}

function idsFromCustomerSelectedItems(raw: AllergyDislikeSelectedItem[]): number[] {
  const out: number[] = [];
  for (const entry of raw) {
    if (typeof entry === 'number' || typeof entry === 'string') {
      const parsed = parseNumericId(entry);
      if (parsed != null) {
        out.push(parsed);
      }
      continue;
    }
    const item_id = resolveInventoryItemId(entry);
    if (item_id > 0) {
      out.push(item_id);
    }
  }
  return out;
}

/** Nested catalog from GET `allergy_dislikes` (`data.categories`). */
export function extractCategoriesFromAllergyDislikeResponse(response: unknown): any[] {
  const root = response as Record<string, any> | null | undefined;
  if (!root) {
    return [];
  }
  const data = root.data ?? root;
  if (data && Array.isArray(data.categories)) {
    return data.categories;
  }
  if (Array.isArray(root.categories)) {
    return root.categories;
  }
  return [];
}

function mergeFlatCategoryMetadata(
  flat_categories: any[],
  flat_sub_categories: any[],
): any[] {
  const sub_categories_by_category = new Map<number, any[]>();

  flat_sub_categories.forEach(sub_category => {
    const category_id = sub_category?.allergy_dislike_category_id;
    if (typeof category_id !== 'number') {
      return;
    }
    const bucket = sub_categories_by_category.get(category_id) ?? [];
    bucket.push({ ...sub_category, items: sub_category?.items ?? [] });
    sub_categories_by_category.set(category_id, bucket);
  });

  return flat_categories.map(category => ({
    ...category,
    sub_categories: sub_categories_by_category.get(category.id) ?? [],
  }));
}

/**
 * Catalog from either:
 * - GET `allergy_dislikes` → `data.categories` (nested, includes items), or
 * - GET `allergy_dislike_categories` → flat lists merged into a tree (no items).
 */
export function extractCategoriesFromCategoriesResponse(
  response: AllergyDislikeCategoriesResponse | AllergyDislikesResponse | undefined,
): any[] {
  if (response == null) {
    return [];
  }

  const nested = extractCategoriesFromAllergyDislikeResponse(response);
  if (nested.length > 0) {
    return nested;
  }

  const data = (response as AllergyDislikeCategoriesResponse).data ?? response;
  const flat_categories = (data as any)?.allergy_dislike_categories;
  const flat_sub_categories = (data as any)?.allergy_dislike_sub_categories;
  if (Array.isArray(flat_categories) && Array.isArray(flat_sub_categories)) {
    return mergeFlatCategoryMetadata(flat_categories, flat_sub_categories);
  }

  return [];
}

/** Selected inventory ids from GET `allergy_dislikes` (`data.customer_selected_items`). */
export function extractSelectedInventoryIdsFromResponse(
  response: AllergyDislikesResponse | undefined,
): number[] | null {
  if (response == null) {
    return null;
  }

  const root = response as Record<string, any>;
  const data = root.data ?? root;

  if (Array.isArray(data?.customer_selected_items)) {
    return idsFromCustomerSelectedItems(data.customer_selected_items);
  }
  if (Array.isArray(root.customer_selected_items)) {
    return idsFromCustomerSelectedItems(root.customer_selected_items);
  }

  const legacy_top = root.customer_selected_inventory_ids ?? root.inventory_ids;
  const legacy_nested = data?.customer_selected_inventory_ids ?? data?.inventory_ids;
  if (Array.isArray(legacy_top)) {
    return normalizeIdList(legacy_top);
  }
  if (Array.isArray(legacy_nested)) {
    return normalizeIdList(legacy_nested);
  }

  return null;
}

export function resolveInventoryItemType(item: any): InventoryPreferenceType | null {
  const raw_type = item?.type ?? item?.preference_type ?? item?.inventory_type ?? item?.category_type;
  if (raw_type === 'allergy' || raw_type === 1 || raw_type === '1') {
    return 'allergy';
  }
  if (raw_type === 'dislike' || raw_type === 2 || raw_type === '2') {
    return 'dislike';
  }
  if (item?.is_allergy === true) {
    return 'allergy';
  }
  if (item?.is_dislike === true) {
    return 'dislike';
  }
  return null;
}

/** Canonical id for selection + POST `inventory_ids[]` — prefer `inventory_id` when present. */
export function resolveInventoryItemId(item: any): number {
  const inventory_id = parseNumericId(item?.inventory_id);
  if (inventory_id != null && inventory_id > 0) {
    return inventory_id;
  }
  const direct = parseNumericId(item?.id);
  if (direct != null && direct > 0) {
    return direct;
  }
  return 0;
}

const INVENTORY_ITEM_KEYS = ['items', 'inventory', 'inventories', 'allergy_dislikes', 'allergies', 'dislikes'] as const;

export function extractInventoryItemsFromSubCategory(sub_category: any): any[] {
  if (!sub_category || typeof sub_category !== 'object') {
    return [];
  }
  for (const key of INVENTORY_ITEM_KEYS) {
    const items = sub_category[key];
    if (Array.isArray(items)) {
      return items;
    }
  }
  return [];
}

/** Map inventory id → allergy/dislike when the API exposes item type (usually absent — unified pool). */
export function buildInventoryTypeMap(categories_raw: any[]): Map<number, InventoryPreferenceType> {
  const type_map = new Map<number, InventoryPreferenceType>();
  if (!Array.isArray(categories_raw)) {
    return type_map;
  }

  categories_raw.forEach(category => {
    const sub_categories = category?.sub_categories ?? category?.subCategories ?? [];
    if (!Array.isArray(sub_categories)) {
      return;
    }
    sub_categories.forEach((sub_category: any) => {
      extractInventoryItemsFromSubCategory(sub_category).forEach(item => {
        const item_id = resolveInventoryItemId(item);
        const item_type = resolveInventoryItemType(item);
        if (item_id > 0 && item_type != null) {
          type_map.set(item_id, item_type);
        }
      });
    });
  });

  return type_map;
}

/**
 * Split selected ids for menu health-code.
 * Unified pool items without type are applied to both allergy and dislike checks.
 */
export function splitInventoryIdsByType(
  inventory_ids: number[],
  type_map: Map<number, InventoryPreferenceType>,
): { allergy_ids: number[]; dislike_ids: number[] } {
  const allergy_ids: number[] = [];
  const dislike_ids: number[] = [];

  inventory_ids.forEach(id => {
    const item_type = type_map.get(id);
    if (item_type === 'dislike') {
      dislike_ids.push(id);
      return;
    }
    if (item_type === 'allergy') {
      allergy_ids.push(id);
      return;
    }
    allergy_ids.push(id);
    dislike_ids.push(id);
  });

  return { allergy_ids, dislike_ids };
}

export function inventoryIdsFromProfile(
  user: { inventory_ids?: number[]; allergy_ids?: number[]; dislike_ids?: number[] } | null,
): number[] {
  if (!user) {
    return [];
  }
  if (Array.isArray(user.inventory_ids) && user.inventory_ids.length > 0) {
    return normalizeIdList(user.inventory_ids);
  }
  const allergy_ids = Array.isArray(user.allergy_ids) ? normalizeIdList(user.allergy_ids) : [];
  const dislike_ids = Array.isArray(user.dislike_ids) ? normalizeIdList(user.dislike_ids) : [];
  if (allergy_ids.length === 0 && dislike_ids.length === 0) {
    return [];
  }
  return [...new Set([...allergy_ids, ...dislike_ids])];
}
